import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const INITIAL = {
  full_name: "",
  legal_entity: "Worldites International Packaging",
  location: "Coimbatore Head Office",
  department: "",
  designation: "",
  employment_category: "probation",
  joining_date: "",
  reporting_manager_id: "",
  email: "",
  mobile: "",
};

export default function Employees() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (q = "") => {
    const response = await api.get("/hrms/employees", {
      params: { q, status: "active" },
    });
    setItems(response.data.items);
  }, []);

  useEffect(() => {
    Promise.all([api.get("/hrms/meta"), load("")])
      .then(([metaResponse]) => setMeta(metaResponse.data))
      .catch(() => toast.error("Unable to load employee management."));
  }, [load]);

  const submit = async () => {
    if (!form.full_name || !form.department || !form.designation || !form.joining_date) {
      toast.error("Complete the employee name, department, designation, and joining date.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/hrms/employees", {
        ...form,
        reporting_manager_id: form.reporting_manager_id || null,
      });
      toast.success(
        "Employee record created. Login access remains separately controlled in User Management.",
      );
      setOpen(false);
      setForm(INITIAL);
      await load(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to create employee record.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="hrms-employees-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">
            HR master data
          </p>
          <h1 className="mt-1 text-4xl font-bold">Employee Management</h1>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="hrms-new-employee-button">
          <Plus className="mr-2 h-4 w-4" /> Add employee
        </Button>
      </div>
      <div className="card-soft flex flex-wrap items-center gap-3 p-4">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && load(search)}
          placeholder="Search employee, ID, email, or mobile"
          className="max-w-md"
          data-testid="hrms-employee-search"
        />
        <Button
          variant="outline"
          onClick={() => load(search)}
          data-testid="hrms-employee-search-button"
        >
          Search
        </Button>
      </div>
      {open && (
        <section className="card-soft p-5" data-testid="hrms-new-employee-form">
          <div className="mb-4 flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">New employee record</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[
              ["Full name", "full_name", "text"],
              ["Department", "department", "text"],
              ["Designation", "designation", "text"],
              ["Date of joining", "joining_date", "date"],
              ["Official email", "email", "email"],
              ["Mobile", "mobile", "tel"],
            ].map(([label, key, type]) => (
              <div key={key}>
                <Label htmlFor={`hrms-${key}`}>{label}</Label>
                <Input
                  id={`hrms-${key}`}
                  type={type}
                  value={form[key]}
                  onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                  data-testid={`hrms-employee-${key}`}
                />
              </div>
            ))}
            <div>
              <Label htmlFor="hrms-entity">Legal entity</Label>
              <select
                id="hrms-entity"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.legal_entity}
                onChange={(event) => setForm({ ...form, legal_entity: event.target.value })}
                data-testid="hrms-employee-entity"
              >
                {(meta?.legal_entities || []).map((entity) => (
                  <option key={entity}>{entity}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="hrms-location">Location</Label>
              <select
                id="hrms-location"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.location}
                onChange={(event) => setForm({ ...form, location: event.target.value })}
                data-testid="hrms-employee-location"
              >
                {(meta?.locations || []).map((location) => (
                  <option key={location}>{location}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="hrms-category">Employment category</Label>
              <select
                id="hrms-category"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.employment_category}
                onChange={(event) => setForm({ ...form, employment_category: event.target.value })}
                data-testid="hrms-employee-category"
              >
                {(meta?.employment_categories || []).map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="hrms-manager">Reporting manager</Label>
              <select
                id="hrms-manager"
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.reporting_manager_id}
                onChange={(event) => {
                  setForm({ ...form, reporting_manager_id: event.target.value });
                }}
                data-testid="hrms-employee-reporting-manager"
              >
                <option value="">No manager assigned</option>
                {(meta?.managers || []).map((manager) => (
                  <option key={manager.user_id} value={manager.user_id}>
                    {manager.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="hrms-employee-cancel-button"
            >
              Cancel
            </Button>
            <Button
              disabled={busy}
              onClick={submit}
              data-testid="hrms-employee-submit-button"
            >
              {busy ? "Saving…" : "Create employee"}
            </Button>
          </div>
        </section>
      )}
      <div className="card-soft overflow-hidden" data-testid="hrms-employee-table">
        <table className="min-w-full text-left text-sm">
          <thead
            className={[
              "border-b border-border bg-muted/50 text-xs uppercase",
              "tracking-wider text-muted-foreground",
            ].join(" ")}
          >
            <tr>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Department</th>
              <th className="hidden px-4 py-3 sm:table-cell">Location</th>
              <th className="hidden px-4 py-3 md:table-cell">Designation</th>
              <th className="hidden px-4 py-3 lg:table-cell">Account</th>
            </tr>
          </thead>
          <tbody>
            {items.map((employee) => (
              <tr key={employee.employee_id} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-3">
                  <Link
                    className="font-semibold text-primary hover:underline"
                    to={`/hrms/employees/${employee.employee_id}`}
                    data-testid={`hrms-employee-link-${employee.employee_id}`}
                  >
                    {employee.full_name}
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {employee.employee_id}
                    </span>
                  </Link>
                </td>
                <td className="px-4 py-3">{employee.department}</td>
                <td className="hidden px-4 py-3 sm:table-cell">{employee.location}</td>
                <td className="hidden px-4 py-3 md:table-cell">{employee.designation}</td>
                <td className="hidden px-4 py-3 lg:table-cell">
                  {employee.user_id ? "Linked" : "Not provisioned"}
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td
                  className="px-4 py-8 text-center text-muted-foreground"
                  colSpan="5"
                  data-testid="hrms-employees-empty"
                >
                  No employee records match this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}